import { useEffect, useRef, useState, type HTMLAttributes, type PointerEvent as RPointerEvent } from 'react';
import { ApiError, call } from '../api';
import { PlayIcon } from '../components/Icons';
import { linkProps, navigate } from '../router';

/**
 * "Wat kijken we vanavond?": iedereen swipet op z'n eigen scherm door dezelfde stapel uit de bibliotheek.
 * Vindt iedereen dezelfde titel leuk, dan is het een match. Alleen swipen kan ook: dan maak je een shortlist.
 */

interface Card { key: string; type: 'movie' | 'show'; title: string; year: number | null; summary: string; rating: number | null; genres: string[]; minutes: number | null; contentRating: string | null; poster: string | null }
interface Room {
  code: string;
  host: string;
  isHost: boolean;
  filters: { kind: 'movie' | 'show' | 'both'; unwatched: boolean; genre: string | null };
  members: { name: string; seen: number; me: boolean }[];
  deck: Card[];
  voted: Record<string, boolean>;
  matches: string[];
  mine: string[];
}

const GENRES: [string, string][] = [
  ['', 'Alle genres'], ['actie', 'Actie'], ['komedie', 'Komedie'], ['drama', 'Drama'], ['thriller', 'Thriller'], ['horror', 'Horror'],
  ['scifi', 'Sciencefiction'], ['fantasy', 'Fantasy'], ['avontuur', 'Avontuur'], ['misdaad', 'Misdaad'], ['romantiek', 'Romantiek'],
  ['animatie', 'Animatie'], ['familie', 'Familie'], ['documentaire', 'Documentaire'],
];

const errText = (e: unknown, fallback: string) => (e instanceof ApiError && e.message !== e.code ? e.message : fallback);

export function Swipe({ code }: { code: string | null }) {
  return code ? <SwipeRoom code={code} /> : <SwipeLobby />;
}

function SwipeLobby() {
  const [kind, setKind] = useState<'movie' | 'show' | 'both'>('movie');
  const [unwatched, setUnwatched] = useState(true);
  const [genre, setGenre] = useState('');
  const [join, setJoin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = () => {
    setBusy(true);
    setError(null);
    call<Room>('/api/swipe', { method: 'POST', body: JSON.stringify({ kind, unwatched, genre: genre || null }) })
      .then((r) => navigate(`/swipe/${r.code}`, { replace: true }))
      .catch((e: unknown) => { setError(errText(e, 'Starten lukte niet.')); setBusy(false); });
  };

  return (
    <main className="page swipe-page">
      <header className="page-head">
        <h1>Wat kijken we vanavond?</h1>
      </header>
      <p className="subtle swipe-intro">Swipe samen door je bibliotheek. Vindt iedereen dezelfde titel leuk, dan heb je een match. Alleen kan ook: dan hou je een shortlist over.</p>
      <div className="swipe-lobby">
        <section className="swipe-box">
          <h2>Nieuwe ronde</h2>
          <div className="filterbar" data-focus-group>
            <button className="chip" aria-pressed={kind === 'movie'} onClick={() => setKind('movie')}>Films</button>
            <button className="chip" aria-pressed={kind === 'show'} onClick={() => setKind('show')}>Series</button>
            <button className="chip" aria-pressed={kind === 'both'} onClick={() => setKind('both')}>Allebei</button>
            <button className="chip" aria-pressed={unwatched} onClick={() => setUnwatched(!unwatched)}>Ongezien</button>
            <select className="chip-select" value={genre} onChange={(e) => setGenre(e.target.value)} aria-label="Genre">
              {GENRES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" data-autofocus disabled={busy} onClick={create}>{busy ? 'Stapel maken…' : 'Starten'}</button>
        </section>
        <section className="swipe-box">
          <h2>Meedoen</h2>
          <form className="swipe-join" onSubmit={(e) => { e.preventDefault(); if (join.trim().length >= 4) navigate(`/swipe/${join.trim().toUpperCase()}`); }}>
            <input className="input swipe-code-input" value={join} maxLength={4} placeholder="CODE" aria-label="Code" autoCapitalize="characters" onChange={(e) => setJoin(e.target.value.replace(/[^a-z]/gi, '').toUpperCase())} />
            <button className="btn" type="submit" disabled={join.length < 4}>Meedoen</button>
          </form>
        </section>
      </div>
      {error && <p className="placeholder-row error" role="alert">{error}</p>}
    </main>
  );
}

function SwipeRoom({ code }: { code: string }) {
  const [room, setRoom] = useState<Room | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [local, setLocal] = useState<Record<string, boolean>>({});
  const [shown, setShown] = useState<Set<string>>(new Set());
  const [match, setMatch] = useState<Card | null>(null);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [fly, setFly] = useState<'left' | 'right' | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    let alive = true;
    let timer = 0;
    const poll = () => {
      call<Room>(`/api/swipe/${code}`).then((r) => alive && setRoom(r)).catch(() => undefined).finally(() => { if (alive) timer = window.setTimeout(poll, 2000); });
    };
    call<Room>(`/api/swipe/${code}/join`, { method: 'POST' })
      .then((r) => { if (!alive) return; setRoom(r); setShown(new Set(r.matches)); timer = window.setTimeout(poll, 2000); })
      .catch((e: unknown) => alive && setError(errText(e, 'Meedoen lukte niet.')));
    return () => { alive = false; window.clearTimeout(timer); };
  }, [code]);

  // Nieuwe match: één keer groot laten zien
  useEffect(() => {
    if (!room) return;
    const fresh = room.matches.find((k) => !shown.has(k));
    if (!fresh) return;
    setShown(new Set([...shown, fresh]));
    const c = room.deck.find((x) => x.key === fresh);
    if (c) setMatch(c);
  }, [room, shown]);

  const voted = { ...(room?.voted ?? {}), ...local };
  const queue = room ? room.deck.filter((c) => !(c.key in voted)) : [];
  const card = queue[0];
  const next = queue[1];

  const vote = (like: boolean) => {
    if (!card || fly) return;
    setFly(like ? 'right' : 'left');
    window.setTimeout(() => {
      setLocal((l) => ({ ...l, [card.key]: like }));
      setFly(null);
      setDrag(null);
    }, 220);
    call<Room>(`/api/swipe/${code}/vote`, { method: 'POST', body: JSON.stringify({ key: card.key, like }) }).then(setRoom).catch(() => undefined);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (match || (e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); vote(false); }
      if (e.key === 'ArrowRight') { e.preventDefault(); vote(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onDown = (e: RPointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: RPointerEvent) => {
    if (start.current) setDrag({ x: e.clientX - start.current.x, y: e.clientY - start.current.y });
  };
  const onUp = () => {
    const dx = drag?.x ?? 0;
    start.current = null;
    if (Math.abs(dx) > 110) vote(dx > 0);
    else setDrag(null);
  };

  const leave = () => {
    call(`/api/swipe/${code}/leave`, { method: 'POST' }).catch(() => undefined);
    navigate('/random');
  };

  if (error) {
    return (
      <main className="page swipe-page">
        <p className="placeholder-row error" role="alert">{error}</p>
        <a className="btn" {...linkProps('/swipe')}>Nieuwe ronde</a>
      </main>
    );
  }
  if (!room) return <main className="page swipe-page"><p className="subtle">Stapel ophalen…</p></main>;

  const solo = room.members.length < 2;
  const byKey = (k: string) => room.deck.find((c) => c.key === k);
  const matches = room.matches.map(byKey).filter((c): c is Card => !!c);
  const mine = room.mine.map(byKey).filter((c): c is Card => !!c);
  const dx = fly === 'right' ? 600 : fly === 'left' ? -600 : drag?.x ?? 0;
  const dy = drag?.y ?? 0;
  const hint = dx > 40 ? 'like' : dx < -40 ? 'nope' : null;

  return (
    <main className="page swipe-page">
      <header className="page-head swipe-head">
        <h1>Wat kijken we vanavond?</h1>
        <div className="swipe-code" aria-label={`Code ${room.code}`}>
          <span className="subtle">Code</span>
          <b>{room.code}</b>
        </div>
      </header>
      <ul className="swipe-members" aria-label="Wie doet mee">
        {room.members.map((m) => (
          <li key={m.name + m.me} className={m.me ? 'is-me' : ''}>
            <span>{m.name}{m.me ? ' (jij)' : ''}</span>
            <i style={{ width: `${Math.round((Math.min(m.seen, room.deck.length) / Math.max(1, room.deck.length)) * 100)}%` }} />
          </li>
        ))}
        {solo && <li className="swipe-invite subtle">Laat anderen meedoen met code <b>{room.code}</b> via Wat zullen we kijken? → Samen swipen.</li>}
      </ul>

      <div className="swipe-stage">
        {card ? (
          <>
            {next && <SwipeCard c={next} className="is-next" />}
            <SwipeCard
              key={card.key}
              c={card}
              hint={hint}
              style={{ transform: `translate(${dx}px, ${dy * 0.3}px) rotate(${dx / 18}deg)`, transition: drag && !fly ? 'none' : undefined, opacity: fly ? 0 : 1 }}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={() => { start.current = null; setDrag(null); }}
            />
          </>
        ) : (
          <div className="swipe-done">
            <h2>Stapel doorgewerkt</h2>
            <p className="subtle">{solo ? 'Dit vond je leuk:' : matches.length ? 'Dit zijn jullie matches.' : 'Nog geen match. Wacht op de rest of begin een nieuwe ronde met andere filters.'}</p>
          </div>
        )}
      </div>

      {card && (
        <div className="swipe-buttons" data-focus-group>
          <button className="swipe-btn is-nope" aria-label="Niet (pijl links)" onClick={() => vote(false)}>✕</button>
          <span className="subtle">{room.deck.length - queue.length + 1} / {room.deck.length}</span>
          <button className="swipe-btn is-like" data-autofocus aria-label="Leuk (pijl rechts)" onClick={() => vote(true)}>♥</button>
        </div>
      )}

      {(solo ? mine : matches).length > 0 && (
        <section className="swipe-results">
          <h2>{solo ? 'Je shortlist' : 'Matches'}</h2>
          <div className="swipe-list">
            {(solo ? mine : matches).map((c) => <ResultCard key={c.key} c={c} />)}
          </div>
        </section>
      )}

      <div className="form-actions">
        <a className="btn" {...linkProps('/swipe')}>Nieuwe ronde</a>
        <button className="btn" onClick={leave}>Stoppen</button>
      </div>

      {match && (
        <div className="swipe-match" role="dialog" aria-modal="true" aria-label="Match">
          <div className="swipe-match-body" data-focus-group>
            <p className="swipe-match-title">Match!</p>
            {match.poster && <img src={match.poster} alt="" />}
            <h2>{match.title}{match.year ? ` (${match.year})` : ''}</h2>
            <p className="subtle">Iedereen vond deze leuk.</p>
            <div className="form-actions">
              {match.type === 'movie' && <button className="btn btn-primary" data-autofocus onClick={() => navigate(`/play/${match.key}`)}><PlayIcon /> Afspelen</button>}
              <a className={`btn ${match.type === 'show' ? 'btn-primary' : ''}`} {...linkProps(`/item/${match.key}`)}>Bekijken</a>
              <button className="btn" onClick={() => setMatch(null)}>Verder swipen</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function meta(c: Card): string {
  return [c.year, c.minutes ? `${c.minutes} min` : null, c.type === 'show' ? 'Serie' : null, c.contentRating?.split('/').pop(), c.rating ? `★ ${c.rating.toFixed(1)}` : null].filter(Boolean).join(' · ');
}

function SwipeCard({ c, hint, className, ...rest }: { c: Card; hint?: 'like' | 'nope' | null; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`swipe-card ${className ?? ''}`} {...rest}>
      {c.poster ? <img src={c.poster} alt="" draggable={false} /> : <span className="swipe-noart">{c.title}</span>}
      {hint && <span className={`swipe-stamp is-${hint}`}>{hint === 'like' ? 'LEUK' : 'NEE'}</span>}
      <div className="swipe-info">
        <h2>{c.title}</h2>
        <p className="swipe-meta">{meta(c)}</p>
        {c.genres.length > 0 && <p className="swipe-genres">{c.genres.join(' · ')}</p>}
        {c.summary && <p className="swipe-summary">{c.summary}</p>}
      </div>
    </div>
  );
}

function ResultCard({ c }: { c: Card }) {
  return (
    <a className="swipe-result" {...linkProps(`/item/${c.key}`)}>
      {c.poster ? <img src={c.poster} alt="" /> : <span className="swipe-noart">{c.title}</span>}
      <span>{c.title}</span>
    </a>
  );
}
